import { c as _c } from "react-compiler-runtime";
import { useEffect, useRef, useState } from 'react';
import { useCellSelectionStore } from '../../redux/reduxHooks';
import { useCellSelectionConfig, useCopySelectionRef, useTrackCellSelection } from '../context/CellSelectionContext';
import { isCellSelectionEnabled } from '../config';
import { getSelectedCellCoords } from '../redux/cellSelectionSlice';
import { copyCellSelection } from '../utils/copyCellSelection';
import { useCopyCellSelection } from '../hooks/useCopyCellSelection';
import { useEndDragOnMouseUp } from '../hooks/useEndDragOnMouseUp';
import { useClearCellSelectionOnExternalInteraction } from '../hooks/useClearCellSelectionOnExternalInteraction';
import { useAutoScrollWhileCellDrag } from '../hooks/useAutoScrollWhileCellDrag';
import { useSuppressTextSelectionWhileCellDrag } from '../hooks/useSuppressTextSelectionWhileCellDrag';
import { jsx as _jsx } from "react/jsx-runtime";
// Host for all cell-selection document listeners (drag-end, external-interaction
// clear, copy) and the off-screen copy staging DOM. Mounted by TableWrapper only
// when cell selection is enabled, so these hooks and listeners are absent entirely
// when cell selection is off. The off-screen staging table renders only the cells
// missing from the live DOM during copy via flushSync; zero cost during normal use.
export const CellSelectionEffects = t0 => {
  const $ = _c(21);
  const {
    columns,
    rows,
    getRowId,
    scrollContainerRef
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [entries, setEntries] = useState(t1);
  const containerRef = useRef(null);
  const config = useCellSelectionConfig();
  const store = useCellSelectionStore();
  const copySelectionRef = useCopySelectionRef();
  const track = useTrackCellSelection();
  let t2;
  if ($[1] !== config) {
    t2 = isCellSelectionEnabled(config) ? config.getCellCopyValue : undefined;
    $[1] = config;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const getCellCopyValue = t2;
  let t3;
  let t4;
  if ($[3] !== columns || $[4] !== copySelectionRef || $[5] !== getCellCopyValue || $[6] !== getRowId || $[7] !== rows || $[8] !== store || $[9] !== track) {
    t3 = () => {
      const copySelection = actionSource => {
        copyCellSelection({
          coords: getSelectedCellCoords(store.getState()),
          columns,
          rows,
          getRowId,
          getCellCopyValue,
          setStagingEntries: setEntries,
          stagingContainerRef: containerRef
        });
        track === null || track === void 0 || track({
          type: "cellSelectionCopied",
          actionSource
        });
      };
      copySelectionRef.current = copySelection;
      return () => {
        if (copySelectionRef.current === copySelection) {
          copySelectionRef.current = null;
        }
      };
    };
    t4 = [store, columns, rows, getRowId, getCellCopyValue, copySelectionRef, track];
    $[3] = columns;
    $[4] = copySelectionRef;
    $[5] = getCellCopyValue;
    $[6] = getRowId;
    $[7] = rows;
    $[8] = store;
    $[9] = track;
    $[10] = t3;
    $[11] = t4;
  } else {
    t3 = $[10];
    t4 = $[11];
  }
  useEffect(t3, t4);
  useEndDragOnMouseUp();
  useClearCellSelectionOnExternalInteraction();
  let t5;
  if ($[12] !== scrollContainerRef) {
    t5 = {
      scrollContainerRef
    };
    $[12] = scrollContainerRef;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  useAutoScrollWhileCellDrag(t5);
  let t6;
  if ($[14] !== scrollContainerRef) {
    t6 = {
      scrollContainerRef
    };
    $[14] = scrollContainerRef;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  useSuppressTextSelectionWhileCellDrag(t6);
  useCopyCellSelection();
  if (entries.length === 0) {
    return null;
  }
  let t7;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      position: "absolute",
      left: "-9999px",
      top: "-9999px",
      visibility: "hidden",
      pointerEvents: "none"
    };
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== entries) {
    t8 = entries.map(_temp);
    $[17] = entries;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t8) {
    t9 = /*#__PURE__*/_jsx("table", {
      "aria-hidden": "true",
      style: t7,
      children: /*#__PURE__*/_jsx("tbody", {
        ref: containerRef,
        children: t8
      })
    });
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  return t9;
};
function _temp(t0) {
  const {
    rowIndex,
    columnIndex,
    row,
    column
  } = t0;
  return /*#__PURE__*/_jsx("tr", {
    children: /*#__PURE__*/_jsx(column.CellComponent, {
      rowIndex: rowIndex,
      columnIndex: columnIndex,
      columnNamespace: column.columnNamespace,
      columnName: column.columnName,
      row: row,
      requiredCellProps: {
        "data-table-external-id": `staging-${rowIndex}-${columnIndex}`,
        "data-test-id": `staging-${rowIndex}-${columnIndex}`,
        "data-fdt-row-index": rowIndex,
        "data-column-index": columnIndex
      }
    })
  }, `${rowIndex}:${columnIndex}`);
}